import { useState } from 'react'
import { API_ENDPOINTS } from '../../config/api'
import { useAuth } from '../AuthProvider'
import { Button } from '../ui/Button'

export type RagIntent =
  | 'governance_document_analysis'
  | 'evidence_gap_analysis'
  | 'policy_consistency_analysis'
  | 'remediation_context_analysis'
  | 'document_rag'

type Citation = {
  chunkId?: string
  documentId?: string
  filename?: string
  pageNumber?: string | number | null
  sectionTitle?: string | null
  excerpt?: string | null
  confidence?: number
  freshness?: number
}

type CitationQualityMeta = {
  averageQuality?: number
  staleCount?: number
  density?: { sufficient?: boolean }
}

type RagResult = {
  analysisRunId: string
  answer?: string | null
  confidence: string
  insufficientContext: boolean
  citations: Citation[]
  retrievalMode: string
  providerScope: string
  citationQuality?: CitationQualityMeta | null
}

type Props = {
  title: string
  intent: RagIntent
  presetQueries: Array<{ label: string; query: string }>
  documentIds?: string[]
  evidenceIds?: string[]
  policyIds?: string[]
  governanceContext?: Record<string, unknown>
}

export default function GovernanceDocumentRagPanel({
  title,
  intent,
  presetQueries,
  documentIds,
  evidenceIds,
  policyIds,
  governanceContext,
}: Props) {
  const { token } = useAuth()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(presetQueries[0]?.query || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RagResult | null>(null)

  // AI-5: Document RAG panel available with the release — auth/RBAC only (no rollout flag).

  const runQuery = async () => {
    if (!token || !query.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch(API_ENDPOINTS.AI_RAG_QUERY, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          intent,
          documentIds,
          evidenceIds,
          policyIds,
          governanceContext,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || 'RAG query failed')
      setResult(json.data as RagResult)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'RAG query failed')
    } finally {
      setLoading(false)
    }
  }

  if (!open) {
    return (
      <Button type="button" size="small" color="outline" onClick={() => setOpen(true)}>
        {title}
      </Button>
    )
  }

  return (
    <section
      className="rounded border border-slate-200 bg-white p-3 space-y-3 text-sm"
      aria-label={title}
      data-testid="governance-document-rag-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-800">{title}</span>
        <Button type="button" size="small" color="outline" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>
      <p className="text-xs text-slate-500 m-0">
        Citation-grounded document analysis only. Not a general chatbot. Answers are policy-aware and org-scoped.
        Quality and freshness indicators are shown when available.
      </p>
      <div className="flex flex-wrap gap-2">
        {presetQueries.map((preset) => (
          <Button
            key={preset.label}
            type="button"
            size="small"
            color="outline"
            onClick={() => setQuery(preset.query)}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      <textarea
        className="input w-full min-h-[72px]"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Ask about supporting documents…"
        disabled={loading}
      />
      <Button type="button" size="small" color="brand" disabled={loading} onClick={() => void runQuery()}>
        {loading ? 'Analyzing…' : 'Run analysis'}
      </Button>
      {error ? (
        <p className="text-red-600 m-0" role="alert">
          {error}
        </p>
      ) : null}
      {result ? (
        <div className="space-y-2 border-t border-slate-100 pt-2">
          <div className="flex flex-wrap gap-2 text-xs text-slate-600">
            <span>Confidence: {result.confidence}</span>
            <span>Mode: {result.retrievalMode}</span>
            <span>Scope: {result.providerScope}</span>
            {result.insufficientContext ? (
              <span className="text-amber-700 font-medium">Insufficient context</span>
            ) : null}
          </div>
          <p className="m-0 whitespace-pre-wrap text-slate-800">{result.answer || 'No answer generated.'}</p>
          <div>
            <div className="font-semibold text-slate-700">Citations</div>
            {!result.citations?.length ? (
              <p className="text-xs text-slate-500 m-0">No grounded citations available.</p>
            ) : (
              <ul className="list-disc pl-5 m-0 space-y-1 text-xs">
                {result.citations.map((c, idx) => (
                  <li key={c.chunkId || idx}>
                    {c.filename || 'document'}
                    {c.sectionTitle ? ` — ${c.sectionTitle}` : ''}
                    {c.pageNumber != null ? ` (p.${c.pageNumber})` : ''}
                    {c.freshness != null && c.freshness < 0.35 ? (
                      <span className="text-amber-700"> [stale]</span>
                    ) : null}
                    {c.excerpt ? `: ${c.excerpt}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </section>
  )
}
